<script setup lang="ts">
import { computed } from 'vue';
import { DgrIcon } from '@stockmarkteam/donguri-ui';
import Feedback from '@/components/home/insights/feedback/feedback.vue';
import {
  useInsightDate,
  useInsightTracking,
} from '@/components/home/insights/use-insight-tracking';
import { INSIGHT_HINT_TITLE } from '@/components/insights/insight-display';
import InsightHintCard from '@/components/insights/insight-hint-card.vue';
import InsightSaveButton from '@/components/insights/insight-save-button.vue';
import type { InsightHint, InsightHintTrackingContext } from '@/types/insights';
import { formatDate } from '@/utils/formatters';
import { featureFlags } from '@/featureFlags';

interface Props {
  hint: InsightHint;
  plain?: boolean;
}
const props = defineProps<Props>();

const isTopicHintPair = featureFlags.ANDEV_10719_INSIGHT_TOPIC_HINT_PAIR;

const insightDate = useInsightDate();
const dateLabel = computed(() =>
  isTopicHintPair && insightDate.value ? formatDate(insightDate.value) : null,
);

const trackingContext = useInsightTracking();
const tracking = computed<InsightHintTrackingContext>(() => ({
  ...trackingContext,
  hintId: props.hint.id,
}));
</script>

<template>
  <InsightHintCard :hint="hint" :plain="plain">
    <template #header>
      <div v-if="isTopicHintPair" class="heading">
        <div class="title paired c-title-medium--m">
          <DgrIcon name="tips" />
          {{ INSIGHT_HINT_TITLE }}
        </div>
        <div v-if="dateLabel" class="c-text-regular--s date">
          {{ dateLabel }}
        </div>
      </div>
      <div v-else class="title c-title-medium--m">
        <DgrIcon name="tips" />
        {{ INSIGHT_HINT_TITLE }}
      </div>

      <div class="actions" :class="{ top: dateLabel }">
        <InsightSaveButton
          :hint-uuid="hint.uuid"
          :initial-saved="hint.saved"
          :tracking="tracking"
        />
        <Feedback :hint-uuid="hint.uuid" />
      </div>
    </template>
  </InsightHintCard>
</template>

<style lang="scss" scoped>
.title {
  display: flex;
  justify-content: center;
}
// DgrIcon のサイズプリセットに 20px が無いため上書きする。
.title.paired .icon-box {
  width: 20px;
  height: 20px;
}
.heading {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;

  .date {
    color: $color-gray800;
  }
}
.actions {
  display: flex;
  gap: 8px;

  // 日付で見出しが 2 行になるため、保存ボタンをタイトル行の高さに揃える。
  &.top {
    align-self: flex-start;
  }
}
</style>
